import { ref } from '@vue/composition-api'
import { PrinterConnection } from '@yonarezi/yonarezi3.epos-printer'
import useCurrentData from '@/views/composable/useCurrentData'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'
import { reportPrinterError } from '@/utils/sentryReportPrinterError'
import usePrinterReconnectOnResume from '@/views/composable/usePrinterReconnectOnResume'

// module-singleton: 複数 Layout / 画面で composable を呼んでも同じ状態・同じ接続を共有する
// 旧実装は呼び出し毎に新規 PrinterConnection を作っており、Layout 切替で接続が乱立していた
const isConnected = ref(null)
const isConnecting = ref(null)
const printer = ref(null)

let printerConnection = null
let currentIpAddress = null
let reconnectListenerRegistered = false

const resolveIpAddress = (printIpAddress, currentClub) => {
  const raw = printIpAddress ?? currentClub.value?.printIpAddress

  return raw ? removePortFromIpAddress(raw) : null
}

export default () => {
  const { currentClub } = useCurrentData()

  const checkConnection = async (printIpAddress = null) => {
    isConnecting.value = true

    const ipAddress = resolveIpAddress(printIpAddress, currentClub)
    if (!ipAddress) {
      isConnecting.value = false

      return
    }

    try {
      // 既存接続を使い回す or IP が変わった場合は新規作成
      if (!printerConnection || currentIpAddress !== ipAddress) {
        printerConnection = new PrinterConnection({ ipAddress })
        currentIpAddress = ipAddress
      }
      printer.value = await printerConnection.connect()
      isConnected.value = !!printer.value.ePosDev.isConnected()
    } catch (error) {
      console.error(error)
      reportPrinterError(error, {
        kind: 'receipt',
        phase: 'connect',
        ipAddress,
        clubId: currentClub.value?.id,
      })
    } finally {
      isConnecting.value = false
    }
  }

  // スリープ復帰・ネットワーク復帰時の明示的な再接続
  // ePOS SDK の自動再接続はゾンビ WebSocket を蓄積する不具合があるため、
  // 古いセッションを明示的に破棄して新規接続を確立する
  const reconnect = async () => {
    if (!printerConnection) return
    const ipAddress = currentIpAddress
    try {
      printer.value = await printerConnection.reconnect()
      isConnected.value = !!printer.value.ePosDev.isConnected()
    } catch (error) {
      console.error(error)
      reportPrinterError(error, {
        kind: 'receipt',
        phase: 'reconnect',
        ipAddress,
        clubId: currentClub.value?.id,
      })
    }
  }

  // 初回 composable 呼び出し時に visibilitychange / online リスナーを登録
  // module-singleton 化により1度しか登録されない
  if (!reconnectListenerRegistered) {
    reconnectListenerRegistered = true
    usePrinterReconnectOnResume(reconnect)
  }

  return {
    // data
    isConnected,
    isConnecting,
    printer,

    // methods
    checkConnection,
    reconnect,
  }
}
